import React, { useContext, useEffect, useState } from "react";
import { getBankByIBAN } from "../../../api/cybwell";
import TextInputField from "../../../pages/shared/components/formik/text-input";
import { isValidSwissIban } from "./validator";
import { ErrorContext } from "../../../route/error/provider";
import InputContainer from "../../layout/page/forms-container/form-item-container/input-container";
import { MaskedInput } from "../masked-input";
import { InputBaseComponentProps } from "@mui/material";

const IBANMaskedInputComponent = React.forwardRef<
  HTMLInputElement,
  InputBaseComponentProps
>((props, ref) => {
  return (
    <MaskedInput
      {...props}
      ref={ref}
      mask="**** **** **** **** **** *"
      definitions={{
        "*": /[0-9A-Za-z]/,
      }}
      overwrite={false}
      prepare={(value) => value.toUpperCase()}
    />
  );
});
IBANMaskedInputComponent.displayName = "IBANMaskedInputComponent";

interface IbanAutocompleteProps {
  iban: string;
  callback: (code: string, name: string) => void;
  label: string;
  dataPath?: string;
}

/**
 * The IBAN Autocomplete Component.
 */
export const IbanAutocomplete: React.FC<IbanAutocompleteProps> = ({
  iban,
  callback,
  dataPath,
  label,
}: IbanAutocompleteProps): React.JSX.Element => {
  const { setError } = useContext(ErrorContext);

  const [bankName, setBankName] = useState<string>("");

  useEffect(() => {
    try {
      if (iban && isValidSwissIban(iban)) {
        getBankByIBAN(iban).then((res) => {
          setBankName(res.name);
          callback(res.code, res.name);
        });
      } else {
        setBankName("");
        callback("", "");
      }
    } catch (error) {
      setError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iban, setError]);

  const resolveName = (name: string): string => {
    return dataPath ? dataPath + name : name;
  };

  return (
    <InputContainer>
      <TextInputField
        forceReRender
        id={resolveName("iban")}
        name={resolveName("iban")}
        placeholder="CH## #### #### #### #### #"
        label={label}
        value={iban}
        CustomInputComponent={IBANMaskedInputComponent}
      />
      {bankName && (
        <TextInputField
          id={resolveName("bankName")}
          name={resolveName("bankName")}
          InputProps={{
            readOnly: true,
          }}
          value={bankName}
        />
      )}
    </InputContainer>
  );
};
